/* ==========================================================================
   CERVA — interactive behaviour for the custom product-page sections.

   Everything here is built as a custom element. Custom elements upgrade
   automatically whenever the browser parses new markup, which is exactly what
   the Shopify Theme Editor does when a section or block is added, removed,
   reordered or re-rendered. That means there is no "run once on DOMContentLoaded"
   code to go stale — no shopify:section:load re-init is required for the
   components themselves.

   The Theme Editor `select` events are still handled so that the block a
   merchant clicks in the sidebar becomes visible (e.g. the hidden tab panel).
   ========================================================================== */
(function () {
  'use strict';

  /* ---------------------------------------------------------------- tabs -- */
  /*  <cerva-tabs>
        <button role="tab" data-cerva-tab="a" aria-selected="true">…</button>
        <div role="tabpanel" data-cerva-panel="a">…</div>
      </cerva-tabs>                                                          */

  if (!customElements.get('cerva-tabs')) {
    customElements.define(
      'cerva-tabs',
      class CervaTabs extends HTMLElement {
        connectedCallback() {
          this.addEventListener('click', this.onClick);
          this.addEventListener('keydown', this.onKeydown);
          // Normalise state on (re)connect so a re-rendered section is consistent.
          const active = this.querySelector('[data-cerva-tab][aria-selected="true"]') || this.querySelector('[data-cerva-tab]');
          if (active) this.select(active.dataset.cervaTab);
        }

        disconnectedCallback() {
          this.removeEventListener('click', this.onClick);
          this.removeEventListener('keydown', this.onKeydown);
        }

        onClick = (event) => {
          const button = event.target.closest('[data-cerva-tab]');
          if (!button || !this.contains(button)) return;
          this.select(button.dataset.cervaTab);
        };

        onKeydown = (event) => {
          if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
          const buttons = Array.from(this.querySelectorAll('[data-cerva-tab]'));
          if (buttons.length < 2) return;
          const current = buttons.findIndex((b) => b.getAttribute('aria-selected') === 'true');
          const next = event.key === 'ArrowRight' ? (current + 1) % buttons.length : (current - 1 + buttons.length) % buttons.length;
          buttons[next].focus();
          this.select(buttons[next].dataset.cervaTab);
          event.preventDefault();
        };

        select(name) {
          if (!name) return;
          this.querySelectorAll('[data-cerva-tab]').forEach((button) => {
            const on = button.dataset.cervaTab === name;
            button.setAttribute('aria-selected', on ? 'true' : 'false');
            button.setAttribute('tabindex', on ? '0' : '-1');
          });
          this.querySelectorAll('[data-cerva-panel]').forEach((panel) => {
            const on = panel.dataset.cervaPanel === name;
            panel.hidden = !on;
            // Only the visible panel's animation should be running: a paused
            // <video> in a hidden panel wastes bandwidth and battery.
            panel.querySelectorAll('video').forEach((video) => {
              if (on) {
                if (video.autoplay) {
                  const played = video.play();
                  if (played && typeof played.catch === 'function') played.catch(() => {});
                }
              } else if (!video.paused) {
                video.pause();
              }
            });
          });
        }
      }
    );
  }

  /* ------------------------------------------------------------- gallery -- */
  /*  <cerva-gallery>
        <div data-cerva-stage><img data-cerva-stage-img …></div>
        <button data-cerva-shot="{url}" data-cerva-alt="…" data-cerva-caption="…">
      </cerva-gallery>
      Purely presentational: this drives the editorial "product reveal"
      gallery. The real PDP gallery is Shopify's own media-gallery block.    */

  if (!customElements.get('cerva-gallery')) {
    customElements.define(
      'cerva-gallery',
      class CervaGallery extends HTMLElement {
        connectedCallback() {
          this.addEventListener('click', this.onClick);
          const first = this.querySelector('[data-cerva-shot][aria-selected="true"]') || this.querySelector('[data-cerva-shot]');
          if (first) this.show(first);
        }

        disconnectedCallback() {
          this.removeEventListener('click', this.onClick);
        }

        onClick = (event) => {
          const button = event.target.closest('[data-cerva-shot]');
          if (!button || !this.contains(button)) return;
          this.show(button);
        };

        show(button) {
          const img = this.querySelector('[data-cerva-stage-img]');
          const caption = this.querySelector('[data-cerva-stage-caption]');
          const src = button.dataset.cervaShot;

          if (img && src) {
            img.src = src;
            if (button.dataset.cervaSrcset) img.srcset = button.dataset.cervaSrcset;
            img.alt = button.dataset.cervaAlt || '';
            img.hidden = false;
          }
          if (caption && button.dataset.cervaCaption) {
            caption.textContent = button.dataset.cervaCaption;
          }
          this.querySelectorAll('[data-cerva-shot]').forEach((b) => {
            b.setAttribute('aria-selected', b === button ? 'true' : 'false');
          });
        }
      }
    );
  }

  /* -------------------------------------------------- theme editor glue -- */

  if (window.Shopify && window.Shopify.designMode) {
    // When a merchant selects a block in the sidebar, reveal it if it lives in a
    // collapsed tab panel or a closed accordion row.
    document.addEventListener('shopify:block:select', (event) => {
      const target = event.target;
      if (!(target instanceof HTMLElement)) return;

      const details = target.closest('details.cerva-faq__item');
      if (details) details.open = true;

      const panel = target.closest('[data-cerva-panel]');
      if (panel) {
        const tabs = panel.closest('cerva-tabs');
        if (tabs && typeof tabs.select === 'function') tabs.select(panel.dataset.cervaPanel);
      }

      // Selecting the tab button itself should also switch to its panel, so a
      // merchant editing a view sees the video they just attached.
      if (target.matches('[data-cerva-tab]')) {
        const tabs = target.closest('cerva-tabs');
        if (tabs && typeof tabs.select === 'function') tabs.select(target.dataset.cervaTab);
      }

      if (target.matches('[data-cerva-shot]')) {
        const gallery = target.closest('cerva-gallery');
        if (gallery && typeof gallery.show === 'function') gallery.show(target);
      }
    });

    document.addEventListener('shopify:block:deselect', (event) => {
      const target = event.target;
      if (target instanceof HTMLElement && target.matches('details.cerva-faq__item') && target.dataset.cervaKeepOpen !== 'true') {
        target.open = false;
      }
    });
  }
})();
